import {
  getDefaultActionStates,
  MESSAGE_ROLE,
  MessageType,
} from '../../components/Message/types/messages';
import { getInitialTranscriptContextState } from './initialTranscriptContextState';
import {
  TranscriptContextActions,
  TranscriptContextActionsMap,
  TranscriptContextState,
  TranscriptContextTypes,
} from './types';

/**
 * Apply a new status to a message. This will alter the local and server states.
 * This is called when a response is received from the server indicating a message reaction success.
 * @param {TranscriptContextState} state
 * @param {TranscriptContextActionsMap[TranscriptContextTypes.REACT_MESSAGE_SUCCESS]} payload
 * @returns Updated messages object
 */
const applyStatusToMessages = (
  state: TranscriptContextState,
  payload: TranscriptContextActionsMap[TranscriptContextTypes.REACT_MESSAGE_SUCCESS],
) => {
  const { messageId, status } = payload;

  return state.messages.map((message: MessageType) => {
    if (message.messageId === messageId) {
      const newStates = {
        ...message.actionStates,
        liked: status,
      };
      return {
        ...message,
        actionStates: newStates,
        localActionStates: newStates,
      };
    }

    return message;
  });
};

/**
 * Apply a new status to the local states of a message. This will alter the local value only.
 * This is called when a user reacts to a message, before the server request is sent.
 * @param {TranscriptContextState} state
 * @param {TranscriptContextActionsMap[TranscriptContextTypes.REACT_MESSAGE_LOCAL]} payload
 * @returns Updated messages object
 */
const applyLocalStatusToMessages = (
  state: TranscriptContextState,
  payload: TranscriptContextActionsMap[TranscriptContextTypes.REACT_MESSAGE_LOCAL],
) => {
  const { messageId, status } = payload;

  return state.messages.map((message: MessageType) => {
    if (message.messageId === messageId) {
      return {
        ...message,
        localActionStates: {
          ...message.localActionStates,
          liked: status,
        },
      };
    }

    return message;
  });
};

/**
 * Apply the server states to the local states of a message. This will alter the local states only.
 * This is called when a response is received from the server indicating a message reaction failure.
 * The actionStates value is the last known value from the server, and is treated as the source of truth.
 * @param {TranscriptContextState} state
 * @param {TranscriptContextActionsMap[TranscriptContextTypes.REACT_MESSAGE_FAIL]} payload
 * @returns Updated messages object
 */
const applyServerStatusToMessages = (
  state: TranscriptContextState,
  payload: TranscriptContextActionsMap[TranscriptContextTypes.REACT_MESSAGE_FAIL],
) => {
  const messageId = payload;

  return state.messages.map((message: MessageType) => {
    if (message.messageId === messageId) {
      return {
        ...message,
        localActionStates: message.actionStates,
      };
    }

    return message;
  });
};

/**
 * Create a reducer for the transcript context.
 * @param {MessageType} initialMessage
 * @returns (state: TranscriptContextState, action: TranscriptContextActions) => TranscriptContextState - The reducer function
 */
export const createTranscriptContextReducer =
  (initialMessage?: MessageType, placeholderMessage?: MessageType) =>
  (
    state: TranscriptContextState,
    action: TranscriptContextActions,
  ): TranscriptContextState => {
    // Store the message to be resent in case of RESEND_MESSAGE
    let oldMessage = '';

    switch (action.type) {
      case TranscriptContextTypes.RESET_CHAT:
        return {
          ...getInitialTranscriptContextState(
            '',
            initialMessage,
            placeholderMessage,
          ),
          loadFailed: false,
          sessionId: action.payload,
          isSessionActive: false,
        };
    case TranscriptContextTypes.LOAD_SESSION:
      return {
        ...getInitialTranscriptContextState(
          '',
          initialMessage,
          placeholderMessage,
        ),
        sessionId: action.payload,
        loadFailed: false,
        isAwaitingServerMessage: true,
        isSessionActive: true,
      };
    case TranscriptContextTypes.UPDATE_MESSAGES:
      return {
          ...state,
          loadFailed: false,
          sessionId: action.payload.sessionId,
          isAwaitingServerMessage: false,
          messages: [
            ...action.payload.messages.map((message) => {
              return {
                ...message,
                role: message.role.toLowerCase() as MESSAGE_ROLE,
                localActionStates: message.actionStates,
              } satisfies MessageType;
            }),
          ],
        };
      case TranscriptContextTypes.START_CHAT:
        return {
          ...state,
          loadFailed: false,
          isAwaitingServerMessage: true,
          isSessionActive: true,
        };
      case TranscriptContextTypes.START_CHAT_FAIL:
        return {
          ...state,
          loadFailed: true,
          messages: state.messages.map((message) => {
            return {
              ...message,
              role: message.role.toLowerCase() as MESSAGE_ROLE,
            } satisfies MessageType;
          }),
        };
      case TranscriptContextTypes.SEND_MESSAGE:
        return {
          ...state,
          loadFailed: false,
          isAwaitingServerMessage: true,
          messages: [
            ...state.messages,
            {
              message: action.payload.message,
              role: MESSAGE_ROLE.USER,
              messageId: action.payload.messageId,
              actionStates: getDefaultActionStates(),
              localActionStates: getDefaultActionStates(),
            } satisfies MessageType,
          ],
        };
      case TranscriptContextTypes.SEND_MESSAGE_FAIL:
        return {
          ...state,
          loadFailed: false,
          isAwaitingServerMessage: false,
          messages: [
            ...state.messages.map((message) => {
              if (message.messageId === action.payload) {
                return {
                  ...message,
                  failed: true,
                } satisfies MessageType;
              }
              return message;
            }),
          ],
        };
      case TranscriptContextTypes.RESEND_MESSAGE:
        return {
          ...state,
          loadFailed: false,
          isAwaitingServerMessage: true,
          messages: [
            ...state.messages.filter((message) => {
              if (message.messageId === action.payload) {
                oldMessage = message.message;
                return false;
              }
              return true;
            }),
            {
              message: oldMessage,
              role: MESSAGE_ROLE.USER,
              messageId: action.payload,
              actionStates: getDefaultActionStates(),
              localActionStates: getDefaultActionStates(),
            } satisfies MessageType,
          ],
        };
      case TranscriptContextTypes.REACT_MESSAGE_LOCAL:
        return {
          ...state,
          // Set local states to new status
          messages: applyLocalStatusToMessages(state, action.payload),
        };
      case TranscriptContextTypes.REACT_MESSAGE_FAIL:
        return {
          ...state,
          // Set local states to last known server states
          messages: applyServerStatusToMessages(state, action.payload),
        };
      case TranscriptContextTypes.REACT_MESSAGE_SUCCESS:
        return {
          ...state,
          // Set local and server states to new status
          messages: applyStatusToMessages(state, action.payload),
        };
      case TranscriptContextTypes.TOGGLE_SOURCES: {
        const messageId = action.payload;
        const expandedSources = new Set(state.expandedSources || []);
        
        if (expandedSources.has(messageId)) {
          expandedSources.delete(messageId);
        } else {
          expandedSources.add(messageId);
        }
        
        return {
          ...state,
          expandedSources,
        };
      }
      default:
        return state;
    }
  };
